import { useState, useEffect, useMemo } from "react";

const EMPTY = { product_name: "", target_quantity: 100, due_date: "", priority: "Medium", status: "Pending", line_id: "none", start_date: "", bom_item_id: "none" };
const DEFAULT_DUE_MS = 2 * 86400000;

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

function fromOrder(order) {
  return {
    product_name: order.product_name || "",
    target_quantity: order.target_quantity || 100,
    due_date: toLocalInput(order.due_date),
    priority: order.priority || "Medium",
    status: order.status || "Pending",
    line_id: order.line_id || "none",
    start_date: toLocalInput(order.start_date),
    bom_item_id: order.bom_item_id || "none",
  };
}

function freshForm(prefill) {
  const now = new Date();
  return { ...EMPTY, start_date: toLocalInput(now.toISOString()), due_date: toLocalInput(new Date(now.getTime() + DEFAULT_DUE_MS).toISOString()), ...(prefill || {}) };
}

// Normalisers keep the payload builder flat (no nested ternaries).
function normalizeBomItemId(bomItemId, isEdit) {
  if (bomItemId && bomItemId !== "none") return bomItemId;
  return isEdit ? "none" : null; // editing: explicit "none" unsets the link; creating: null
}

export function buildOrderPayload(form, isEdit) {
  return {
    product_name: form.product_name,
    target_quantity: Number(form.target_quantity),
    due_date: new Date(form.due_date).toISOString(),
    priority: form.priority,
    status: form.status,
    line_id: form.line_id === "none" ? null : form.line_id,
    start_date: form.start_date ? new Date(form.start_date).toISOString() : new Date().toISOString(),
    bom_item_id: normalizeBomItemId(form.bom_item_id, isEdit),
  };
}

function labelFor(saving, isEdit) {
  if (saving) return "Saving...";
  return isEdit ? "Save Changes" : "Create Order";
}

export function useOrderForm({ open, order, prefill, bomItems, onSubmit, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const manufacturedItems = useMemo(() => bomItems.filter((b) => b.item_type === "manufactured" && b.status !== "archived" && (b.approval_status || "Active") === "Active"), [bomItems]);

  useEffect(() => {
    setForm(order ? fromOrder(order) : freshForm(prefill));
  }, [order, open, prefill, setForm]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const hasBomLink = form.bom_item_id && form.bom_item_id !== "none";
  let productValue = "";
  if (hasBomLink) productValue = form.bom_item_id;
  else if (form.product_name) productValue = "__custom__";

  const onSelectProduct = (v) => {
    if (v === "__custom__") return;
    const item = manufacturedItems.find((b) => b.id === v);
    setForm((f) => ({ ...f, bom_item_id: v, product_name: item ? item.name : f.product_name }));
  };

  const selectedProduct = manufacturedItems.find((b) => b.id === form.bom_item_id);
  const productUnit = selectedProduct?.unit || "";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit(buildOrderPayload(form, !!order));
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return { form, set, saving, submit, manufacturedItems, productValue, productUnit, onSelectProduct, submitLabel: labelFor(saving, !!order) };
}
